import _ from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eventbrite/i18n';
import Package from './_internal/Package';
import PackagesTabSelection from './_internal/PackagesTabSelection';
import Heading from './Heading';
import {
    HEADING_PROP_TYPES,
    HEADING_PROPS,
    PRICING_PROP_TYPES,
    PRICING_PROPS,
} from '../constants/componentPropTypes';
import { DEFAULT_PACKAGE_TYPES } from '../constants/main';
import './packages.scss';

const PackagesList = ({
    packages,
    mainIndex,
    pricing,
    taxLabel,
    isCopy,
    shouldShowLearnMore,
    shouldShowNewTag,
    onLearnMore,
    CTAs,
    CTAActions,
    CTAStyles,
    ...props
}) => {
    const items = DEFAULT_PACKAGE_TYPES.map((value, index) => {
        const packageInfo = packages[value];
        const selected = mainIndex === index;
        const onClick = CTAActions[index];

        return (
            <Package
                {...props}
                {...packageInfo}
                key={`${value.toString()}`}
                price={pricing[value]}
                taxLabel={taxLabel[value]}
                selected={selected}
                primaryCTA={CTAs[index]}
                CTAStyle={CTAStyles[index]}
                onClick={onClick}
                shouldShowLearnMore={shouldShowLearnMore}
                shouldShowNewTag={shouldShowNewTag}
                onLearnMore={onLearnMore(index)}
                isCopy={isCopy}
            />
        );
    });

    return (
        <div className="package-list eds-l-mar-hor-12 eds-l-mar-top-10 eds-l-mar-bot-16 eds-show-up-md">
            {items}
        </div>
    );
};

export default class Packages extends PureComponent {
    static propTypes = {
        ...HEADING_PROP_TYPES,
        ...PRICING_PROP_TYPES,
        /*
        Fine print at the bottom of the list,
        */
        finePrint: translationPropType,
        /*
        Package index to highlight
        */
        mainIndex: PropTypes.number,
        /*
        Selected package name (Overrides mainIndex for initial selectedIndex)
        */
        selectedPackage: PropTypes.string,
        /*
        CTAs associated with each package
        */
        CTAs: PropTypes.arrayOf(translationPropType),
        /*
        Actions to be triggered from selecting main CTA button
        */
        CTAActions: PropTypes.arrayOf(PropTypes.func),
        /*
        Styles associated with each primary CTA button
        */
        CTAStyles: PropTypes.arrayOf(PropTypes.string),
        /*
        Whether the package should display the Learn More footer component or not
        */
        shouldShowLearnMore: PropTypes.bool,
        /*
        Depending on the locale and the package we should display the "New" tag
        */
        shouldShowNewTag: PropTypes.bool,
        /*
        Actions to be triggered from selecting Learn More on each package
        */
        learnMoreActions: PropTypes.arrayOf(PropTypes.func),
        /*
        Detects whether or not the user came from trying to copy an event
        */
        isCopy: PropTypes.bool,
    };

    static defaultProps = {
        mainIndex: 1,
    };

    constructor(props) {
        super(props);

        const { mainIndex, selectedPackage } = props;
        const startingIndex = this._getStartingSelectedIndex(
            selectedPackage,
            mainIndex,
        );

        this.state = {
            selectedIndex: startingIndex,
        };
    }

    _getStartingSelectedIndex(packageName, defaultIndex) {
        if (!packageName) {
            return defaultIndex;
        }

        const indexFromSelectedPackage =
            DEFAULT_PACKAGE_TYPES.indexOf(packageName);

        if (indexFromSelectedPackage >= 0) {
            return indexFromSelectedPackage;
        }

        return defaultIndex;
    }

    _handleLearnMore(index) {
        const { learnMoreActions } = this.props;

        if (learnMoreActions) {
            return learnMoreActions[index];
        }

        return null;
    }

    _handleClick(selectedIndex) {
        this.setState({
            selectedIndex,
        });
    }

    render() {
        const {
            packages,
            pricing,
            taxLabel,
            finePrint,
            mainIndex,
            isCopy,
            CTAs,
            CTAActions,
            CTAStyles,
            shouldShowLearnMore,
            shouldShowNewTag,
            ...props
        } = this.props;

        const { selectedIndex } = this.state;

        if (!packages) {
            return null;
        }

        const headingProps = _.pick(props, HEADING_PROPS);
        const pricingProps = _.pick(props, PRICING_PROPS);
        const selectedPackage = DEFAULT_PACKAGE_TYPES[selectedIndex];
        const onClick = CTAActions[selectedIndex];
        const selected = mainIndex === selectedIndex;
        const price = pricing && pricing[selectedPackage];
        const extraTaxLabel = taxLabel && taxLabel[selectedPackage];
        let finePrintText = null;

        if (finePrint) {
            finePrintText = (
                <p className="eds-g-cell eds-g-cell-1-1 eds-text--center eds-text-bs eds-text-color--grey-600">
                    {finePrint}
                </p>
            );
        }

        return (
            <div className="eds-g-cell eds-g-cell-1-1">
                <div
                    className="marketing-container__max-width"
                    data-automation="assortments-create-package"
                >
                    <Heading {...headingProps} alignCenter={true} />
                    <PackagesList
                        {...pricingProps}
                        packages={packages}
                        mainIndex={selectedIndex}
                        CTAs={CTAs}
                        CTAStyles={CTAStyles}
                        CTAActions={CTAActions}
                        pricing={pricing}
                        taxLabel={taxLabel}
                        shouldShowLearnMore={shouldShowLearnMore}
                        shouldShowNewTag={shouldShowNewTag}
                        onLearnMore={this._handleLearnMore.bind(this)}
                        isCopy={isCopy}
                    />
                    <PackagesTabSelection
                        packages={packages}
                        selectedPackage={selectedPackage}
                        pricingProps={pricingProps}
                        primaryCTA={CTAs[selectedIndex]}
                        CTAStyle={CTAStyles[selectedIndex]}
                        smallScreen={true}
                        selected={selected}
                        price={price}
                        extraTaxLabel={extraTaxLabel}
                        onClick={onClick}
                        onLearnMore={this._handleLearnMore(selectedIndex)}
                        tabClick={this._handleClick.bind(this)}
                        selectedIndex={selectedIndex}
                    />
                    {finePrintText}
                </div>
            </div>
        );
    }
}
